import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAssessmentSession } from "../../contexts/assessmentUserContext";
import { getApiUrls } from "../../lib/apiUrl";
import { listAssessmentBenchmarkAssetsRequest } from "../../services/assessmentContentService";
import { useAssessmentBuilderSave } from "./useAssessmentBuilderSave";
import { AssessmentQuestionPreview } from "./AssessmentQuestionPreview";
import { BenchmarkAssetUploadDialog } from "./BenchmarkAssetUploadDialog";
import { MazeQuestion as ReadingMazePreview } from "../Assessment/ReadingAssessment/steps/MazeQuestion";
import { VocabularyQuestion as ReadingVocabularyPreview } from "../Assessment/ReadingAssessment/steps/VocabularyQuestion";
import type { ReadingAssessmentContent } from "../Assessment/ReadingAssessment/steps/types";

export type MazeQuestion = {
  id: string;
  placeholderId: string;
  correctWord: string;
  options: [string, string, string, string];
};

export type VocabularyQuestion = {
  id: string;
  placeholderId: string;
  word: string;
  prompt: string;
  A: string;
  B: string;
  C: string;
  D: string;
  correctOption: "A" | "B" | "C" | "D";
};

type MediaItem = {
  id: string;
  label: string;
  assetType: "images" | "audio";
  url: string;
  imageUrl: string;
  audioUrl: string;
  altText: string;
};

type ReadingAssessmentMeta = {
  difficultyLevel: string;
  assessmentTitle: string;
  overallInstructions: string;
  instructionsAudio: string;
  oralReadingInstructionAudio: string;
  instructionImage: string;
  recapImage: string;
  mazeInstructions: string;
  mazeInstructionsAudio: string;
  vocabQuestionInstructionAudio: string;
  recapInstructions: string;
  recapInstructionsAudio: string;
};

type ReadingAssessmentSaveMeta = Omit<ReadingAssessmentMeta, "difficultyLevel">;

export type ReadingPart = {
  title: string;
  promptImage: string;
  passage: string;
  mazeMaskText: string;
  vocabularyMaskText: string;
  mazeBank: MazeQuestion[];
  vocabularyBank: VocabularyQuestion[];
};

type TestDetailsResponse = {
  test: {
    pk: string;
    items: Array<Record<string, unknown>>;
  };
};

type ReadingTestTransferFile = {
  version: string;
  exportedAt: string;
  test: {
    pk?: string;
    difficulty_level: string;
    test_type: "reading";
    status: string;
    benchmark: string;
    meta: ReadingAssessmentSaveMeta;
    parts: Array<{
      part: number;
      title: string;
      prompt_image?: string;
      text_passage: string;
      maze_mask_text: string;
      vocab_mask_text: string;
      questions: {
        maze: Array<{
          id: string;
          placeholderId?: string;
          placeholder_id?: string;
          correctWord: string;
          options: string[];
        }>;
        vocabulary: VocabularyQuestion[];
      };
    }>;
  };
};

const createLocalId = (): string => globalThis.crypto.randomUUID();

const createMazeQuestion = (): MazeQuestion => ({
  id: createLocalId(),
  placeholderId: "",
  correctWord: "",
  options: ["", "", "", ""],
});

const createVocabularyQuestion = (): VocabularyQuestion => {
  return {
    id: createLocalId(),
    placeholderId: "",
    word: "",
    prompt: "",
    A: "",
    B: "",
    C: "",
    D: "",
    correctOption: "A",
  };
};

const createPart = (index: number): ReadingPart => ({
  title: `Part ${index + 1}`,
  promptImage: "",
  passage: "",
  mazeMaskText: "",
  vocabularyMaskText: "",
  mazeBank: [createMazeQuestion()],
  vocabularyBank: [createVocabularyQuestion()],
});

const createInitialParts = (): ReadingPart[] => [
  createPart(0),
  createPart(1),
  createPart(2),
];

const initialMeta: ReadingAssessmentMeta = {
  difficultyLevel: "",
  assessmentTitle: "",
  overallInstructions: "",
  instructionsAudio: "",
  oralReadingInstructionAudio: "",
  instructionImage: "",
  recapImage: "",
  mazeInstructions: "",
  mazeInstructionsAudio: "",
  vocabQuestionInstructionAudio: "",
  recapInstructions: "",
  recapInstructionsAudio: "",
};

const difficultyLevelOptions: { value: string; label: string }[] = [
  { value: "A", label: "Level A - Most Beginner" },
  { value: "B", label: "Level B - Emerging" },
  { value: "C", label: "Level C - 2/3" },
  { value: "D", label: "Level D - 4/5" },
  { value: "E", label: "Level E - 6/7" },
  { value: "F", label: "Level F - 8/9" },
  { value: "G", label: "Level G - Most Advanced" },
];

const toReadingSaveMeta = ({
  difficultyLevel,
  ...saveMeta
}: ReadingAssessmentMeta): ReadingAssessmentSaveMeta => {
  void difficultyLevel;
  return saveMeta;
};

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";
const asStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const extractOptions = (
  item: Record<string, unknown>,
): [string, string, string, string] => {
  const list = asStringList(item.options ?? item.answers);
  return [list[0] ?? "", list[1] ?? "", list[2] ?? "", list[3] ?? ""];
};

const resolveCorrectOption = (
  item: Record<string, unknown>,
): "A" | "B" | "C" | "D" => {
  const candidate = asString(
    item.correct_option ??
      item.correctOption ??
      item.answer ??
      item.correct_answer,
  ).toUpperCase();
  if (candidate === "B" || candidate === "C" || candidate === "D") {
    return candidate;
  }
  return "A";
};

const partIndexFromQuestion = (item: Record<string, unknown>): number => {
  const explicitPart = Number(asString(item.part_order));
  if (!Number.isNaN(explicitPart) && explicitPart >= 1 && explicitPart <= 3) {
    return explicitPart - 1;
  }

  const sk = asString(item.SK);
  const match = sk.match(/^QUESTION#(\d+)#/);
  if (match) {
    const parsed = Number(match[1]);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 3) {
      return parsed - 1;
    }
  }
  return 0;
};

const parseDifficultyLevelFromPK = (pk: string): string => {
  const match = pk.trim().match(/^TEST#TYPE#[^#]+#DIFFICULTY#([^#]+)/i);
  return match?.[1] ?? "";
};

const parseBenchmarkFromPK = (pk: string): string => {
  const match = pk.trim().match(/#BENCHMARK#([^#]+)$/i);
  return match?.[1] ?? "";
};

export const resolveReadingImportTarget = ({
  currentBenchmark,
  importedBenchmark,
  importedPk,
  isDemo,
}: {
  currentBenchmark: string;
  importedBenchmark: string;
  importedPk: string;
  isDemo: string;
}): { benchmark: string; existingTestPk: string } => {
  const READING_DEMO_BENCHMARK = "reading-demo";
  const normalizedIsDemo = isDemo.trim().toLowerCase();
  const isReadingDemoTarget =
    currentBenchmark.trim() === READING_DEMO_BENCHMARK ||
    normalizedIsDemo === "true";

  if (isReadingDemoTarget) {
    return {
      benchmark: READING_DEMO_BENCHMARK,
      existingTestPk: "",
    };
  }

  return {
    benchmark:
      parseBenchmarkFromPK(importedPk) ||
      importedBenchmark.trim() ||
      currentBenchmark.trim(),
    existingTestPk: importedPk,
  };
};

const normalizeCorrectOptionInput = (
  value: unknown,
): VocabularyQuestion["correctOption"] => {
  const candidate = asString(value).toUpperCase();
  if (candidate === "B" || candidate === "C" || candidate === "D") {
    return candidate;
  }
  return "A";
};

const normalizeMazeOptionsInput = (
  value: unknown,
): [string, string, string, string] => {
  const list = asStringList(value);
  return [list[0] ?? "", list[1] ?? "", list[2] ?? "", list[3] ?? ""];
};

const normalizeSearch = (value: string): string => value.trim().toLowerCase();

const matchesSearch = (
  search: string,
  values: Array<string | number | undefined>,
): boolean => {
  const normalizedSearch = normalizeSearch(search);
  if (!normalizedSearch) {
    return true;
  }

  return values.some((value) =>
    String(value ?? "")
      .toLowerCase()
      .includes(normalizedSearch),
  );
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const readingMaskPlaceholderPattern = /\{\{\s*(.+?)\s*}}/g;

const getBuilderPlaceholderId = (question: {
  id: string;
  placeholderId?: string;
}): string => question.placeholderId?.trim() || question.id.trim();

type ReadingMaskValidationError = {
  part: number;
  section: "passage" | "maze" | "vocabulary";
  message: string;
};

const isEmojiCodePoint = (value: string): boolean => {
  const codePoint = value.codePointAt(0);
  if (codePoint === undefined) {
    return false;
  }

  return (
    (codePoint >= 0x1f000 && codePoint <= 0x1faff) ||
    (codePoint >= 0x2600 && codePoint <= 0x27bf) ||
    (codePoint >= 0x2300 && codePoint <= 0x23ff)
  );
};

const getPassageEmojis = (passage: string): string[] => {
  const emojis = new Set<string>();
  for (const codePoint of passage) {
    if (isEmojiCodePoint(codePoint)) {
      emojis.add(codePoint);
    }
  }
  return [...emojis];
};

const validatePassageHasNoEmojis = (
  partNumber: number,
  passage: string,
): ReadingMaskValidationError[] => {
  const emojis = getPassageEmojis(passage);
  if (emojis.length === 0) {
    return [];
  }

  return [
    {
      part: partNumber,
      section: "passage",
      message: `Part ${partNumber} passage contains unsupported emoji: ${emojis.map((emoji) => `"${emoji}"`).join(", ")}. Remove emoji from the passage before saving or exporting.`,
    },
  ];
};

const replaceMaskPlaceholders = (
  maskedText: string,
  replacementsByQuestionId: Map<string, string>,
): string =>
  maskedText.replace(
    readingMaskPlaceholderPattern,
    (rawPlaceholder: string, questionId: string) =>
      replacementsByQuestionId.get(questionId.trim()) ?? rawPlaceholder,
  );

const visibleWhitespace = (value: string): string =>
  value.replace(/\n/g, "\\n").replace(/\t/g, "\\t").replace(/ /g, "·");

const formatMismatchPreview = (value: string, mismatchIndex: number): string => {
  const previewLength = 10;
  if (mismatchIndex >= value.length) {
    return "end of text";
  }
  const end = Math.min(value.length, mismatchIndex + previewLength);
  const suffix = end < value.length ? "..." : "";
  return `"${visibleWhitespace(value.slice(mismatchIndex, end))}${suffix}"`;
};

const describeFirstTextMismatch = (
  expected: string,
  actual: string,
): string => {
  const maxLength = Math.max(expected.length, actual.length);
  let mismatchIndex = 0;
  while (
    mismatchIndex < maxLength &&
    expected[mismatchIndex] === actual[mismatchIndex]
  ) {
    mismatchIndex += 1;
  }

  return `First mismatch at character ${mismatchIndex + 1}: passage has ${formatMismatchPreview(expected, mismatchIndex)}, mask result has ${formatMismatchPreview(actual, mismatchIndex)}.`;
};

const getMaskPlaceholderIds = (maskedText: string): Set<string> => {
  const ids = new Set<string>();
  for (
    let match = readingMaskPlaceholderPattern.exec(maskedText);
    match;
    match = readingMaskPlaceholderPattern.exec(maskedText)
  ) {
    ids.add((match[1] ?? "").trim());
  }
  return ids;
};

const validateMaskExactMatch = ({
  partNumber,
  section,
  passage,
  maskedText,
  replacementsByQuestionId,
}: {
  partNumber: number;
  section: "maze" | "vocabulary";
  passage: string;
  maskedText: string;
  replacementsByQuestionId: Map<string, string>;
}): ReadingMaskValidationError[] => {
  if (!maskedText.trim()) {
    return [];
  }

  const errors: ReadingMaskValidationError[] = [];
  const placeholderIds = getMaskPlaceholderIds(maskedText);
  const missingQuestionIds = [...replacementsByQuestionId.keys()].filter(
    (questionId) => questionId && !placeholderIds.has(questionId),
  );
  if (missingQuestionIds.length > 0) {
    errors.push({
      part: partNumber,
      section,
      message: `Part ${partNumber} ${section} mask is missing placeholders for question IDs: ${missingQuestionIds.join(", ")}`,
    });
  }

  const unknownPlaceholderIds = [...placeholderIds].filter(
    (questionId) => !replacementsByQuestionId.has(questionId),
  );
  if (unknownPlaceholderIds.length > 0) {
    errors.push({
      part: partNumber,
      section,
      message: `Part ${partNumber} ${section} mask references unknown question IDs: ${unknownPlaceholderIds.join(", ")}`,
    });
  }

  const reconstructedMaskText = replaceMaskPlaceholders(
    maskedText,
    replacementsByQuestionId,
  );
  if (reconstructedMaskText !== passage) {
    errors.push({
      part: partNumber,
      section,
      message: `Part ${partNumber} ${section} mask does not exactly match the passage after replacing placeholders with configured answers. ${describeFirstTextMismatch(passage, reconstructedMaskText)}`,
    });
  }

  return errors;
};

export const validateReadingMasksExactMatchPassages = (
  parts: ReadingPart[],
): ReadingMaskValidationError[] =>
  parts.flatMap((part, index) => {
    const partNumber = index + 1;
    const passageEmojiErrors = validatePassageHasNoEmojis(
      partNumber,
      part.passage,
    );
    if (passageEmojiErrors.length > 0) {
      return passageEmojiErrors;
    }

    return [
      ...validateMaskExactMatch({
        partNumber,
        section: "maze",
        passage: part.passage,
        maskedText: part.mazeMaskText,
        replacementsByQuestionId: new Map(
          part.mazeBank.map((question) => [
            getBuilderPlaceholderId(question),
            question.correctWord,
          ]),
        ),
      }),
      ...validateMaskExactMatch({
        partNumber,
        section: "vocabulary",
        passage: part.passage,
        maskedText: part.vocabularyMaskText,
        replacementsByQuestionId: new Map(
          part.vocabularyBank.map((question) => [
            getBuilderPlaceholderId(question),
            question.word,
          ]),
        ),
      }),
    ];
  });

const formatReadingMaskValidationErrors = (
  errors: ReadingMaskValidationError[],
): string => errors.map((error) => error.message).join("\n");

const resolveReadingPreviewImageUrl = (
  mediaLibrary: MediaItem[],
  imageIdOrUrl: string,
): string => {
  const trimmed = imageIdOrUrl.trim();
  if (!trimmed) {
    return "";
  }

  return (
    mediaLibrary.find((item) => item.id === trimmed)?.imageUrl ||
    mediaLibrary.find((item) => item.url === trimmed)?.imageUrl ||
    trimmed
  );
};

const resolvePreviewMaskedText = (
  maskedText: string,
  plainText: string,
  placeholderId: string,
  targetText: string,
): string => {
  const placeholder = `{{${placeholderId}}}`;
  if (
    maskedText.includes(placeholder) ||
    maskedText.includes(`{{ ${placeholderId} }}`)
  ) {
    return maskedText;
  }

  const escapedTargetText = targetText.trim()
    ? escapeRegExp(targetText.trim())
    : "";
  if (escapedTargetText) {
    const targetPattern = new RegExp(escapedTargetText, "i");
    if (targetPattern.test(plainText)) {
      return plainText.replace(targetPattern, `{{ ${placeholderId} }}`);
    }
  }

  return maskedText || plainText;
};

const buildMazePreviewAssessment = (
  meta: ReadingAssessmentMeta,
  mediaLibrary: MediaItem[],
  part: ReadingPart,
  partIndex: number,
  question: MazeQuestion,
  questionIndex: number,
): ReadingAssessmentContent => {
  const questionId =
    question.id.trim() || `preview-maze-${partIndex + 1}-${questionIndex + 1}`;
  const placeholderId =
    question.placeholderId.trim() ||
    `preview-maze-placeholder-${partIndex + 1}-${questionIndex + 1}`;

  return {
    testType: "reading",
    overallInstructions: meta.overallInstructions,
    instructionsAudio: meta.instructionsAudio,
    oralReadingInstructionAudio: meta.oralReadingInstructionAudio,
    mazeInstructions: meta.mazeInstructions,
    mazeInstructionsAudio: meta.mazeInstructionsAudio,
    vocabQuestionInstructionAudio: meta.vocabQuestionInstructionAudio,
    recapInstructions: meta.recapInstructions,
    recapInstructionsAudio: meta.recapInstructionsAudio,
    instructionImage: resolveReadingPreviewImageUrl(
      mediaLibrary,
      meta.instructionImage,
    ),
    recapImage: resolveReadingPreviewImageUrl(mediaLibrary, meta.recapImage),
    part: {
      testType: "reading",
      submissionId: "preview",
      partOrder: partIndex + 1,
      prompt: part.passage,
      promptImage: resolveReadingPreviewImageUrl(
        mediaLibrary,
        part.promptImage,
      ),
      mazeMaskedPrompt: resolvePreviewMaskedText(
        part.mazeMaskText,
        part.passage,
        placeholderId,
        question.correctWord,
      ),
      vocabMaskedPrompt: part.vocabularyMaskText,
      questions: [
        {
          question_id: questionId,
          placeholder_id: placeholderId,
          question_type: "maze",
          part_order: partIndex + 1,
          question_order: questionIndex + 1,
          options: question.options.filter(Boolean),
        },
      ],
    },
    submissionProgress: {
      submission_id: "preview",
      status: "draft",
      answers_by_question_id: {},
    },
  };
};

const buildVocabularyPreviewAssessment = (
  meta: ReadingAssessmentMeta,
  mediaLibrary: MediaItem[],
  part: ReadingPart,
  partIndex: number,
  question: VocabularyQuestion,
  questionIndex: number,
): ReadingAssessmentContent => {
  const questionId =
    question.id.trim() || `preview-vocab-${partIndex + 1}-${questionIndex + 1}`;
  const placeholderId =
    question.placeholderId.trim() ||
    `preview-vocab-placeholder-${partIndex + 1}-${questionIndex + 1}`;

  return {
    testType: "reading",
    overallInstructions: meta.overallInstructions,
    instructionsAudio: meta.instructionsAudio,
    oralReadingInstructionAudio: meta.oralReadingInstructionAudio,
    mazeInstructions: meta.mazeInstructions,
    mazeInstructionsAudio: meta.mazeInstructionsAudio,
    vocabQuestionInstructionAudio: meta.vocabQuestionInstructionAudio,
    recapInstructions: meta.recapInstructions,
    recapInstructionsAudio: meta.recapInstructionsAudio,
    instructionImage: resolveReadingPreviewImageUrl(
      mediaLibrary,
      meta.instructionImage,
    ),
    recapImage: resolveReadingPreviewImageUrl(mediaLibrary, meta.recapImage),
    part: {
      testType: "reading",
      submissionId: "preview",
      partOrder: partIndex + 1,
      prompt: part.passage,
      promptImage: resolveReadingPreviewImageUrl(
        mediaLibrary,
        part.promptImage,
      ),
      mazeMaskedPrompt: part.mazeMaskText,
      vocabMaskedPrompt: resolvePreviewMaskedText(
        part.vocabularyMaskText,
        part.passage,
        placeholderId,
        question.word,
      ),
      questions: [
        {
          question_id: questionId,
          placeholder_id: placeholderId,
          question_type: "vocabulary",
          part_order: partIndex + 1,
          question_order: questionIndex + 1,
          options: [question.A, question.B, question.C, question.D].filter(
            Boolean,
          ),
          highlightedText: question.word,
        },
      ],
    },
    submissionProgress: {
      submission_id: "preview",
      status: "draft",
      answers_by_question_id: {},
    },
  };
};

const toReadingTransferFile = (
  existingTestPk: string,
  benchmark: string,
  meta: ReadingAssessmentMeta,
  parts: ReadingPart[],
): ReadingTestTransferFile => ({
  version: "reading-assessment-builder/v2",
  exportedAt: new Date().toISOString(),
  test: {
    pk: existingTestPk || undefined,
    difficulty_level: meta.difficultyLevel,
    test_type: "reading",
    status: "draft",
    benchmark: benchmark,
    meta: toReadingSaveMeta(meta),
    parts: parts.map((part, index) => ({
      part: index + 1,
      title: part.title,
      prompt_image: part.promptImage,
      text_passage: part.passage,
      maze_mask_text: part.mazeMaskText,
      vocab_mask_text: part.vocabularyMaskText,
      questions: {
        maze: part.mazeBank.map((question) => ({
          id: question.id,
          placeholderId: question.placeholderId,
          placeholder_id: question.placeholderId,
          correctWord: question.correctWord,
          options: question.options,
        })),
        vocabulary: part.vocabularyBank.map((question) => ({
          ...question,
          placeholder_id: question.placeholderId,
        })),
      },
    })),
  },
});

export function ReadingAssessmentBuilderPage() {
  const { search } = useLocation();
  const { token } = useAssessmentSession();
  const { assessmentsApiUrl: apiUrl } = getApiUrls();

  const [meta, setMeta] = useState<ReadingAssessmentMeta>(initialMeta);
  const [parts, setParts] = useState<ReadingPart[]>(createInitialParts);
  const [mediaLibrary, setMediaLibrary] = useState<MediaItem[]>([]);
  const [loadingExistingTest, setLoadingExistingTest] =
    useState<boolean>(false);
  const [existingTestError, setExistingTestError] = useState<string | null>(
    null,
  );
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("meta");
  const [collapsedMazeQuestions, setCollapsedMazeQuestions] = useState<
    Record<string, boolean>
  >({});
  const [collapsedVocabularyQuestions, setCollapsedVocabularyQuestions] =
    useState<Record<string, boolean>>({});
  const [visibleMazePreviews, setVisibleMazePreviews] = useState<
    Record<string, boolean>
  >({});
  const [visibleVocabularyPreviews, setVisibleVocabularyPreviews] = useState<
    Record<string, boolean>
  >({});
  const [mediaLibrarySearch, setMediaLibrarySearch] = useState<string>("");
  const [mazeSearchByPart, setMazeSearchByPart] = useState<
    Record<number, string>
  >({});
  const [vocabularySearchByPart, setVocabularySearchByPart] = useState<
    Record<number, string>
  >({});
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [isAssetUploadDialogOpen, setIsAssetUploadDialogOpen] =
    useState<boolean>(false);
  const [benchmarkAssetsError, setBenchmarkAssetsError] = useState<
    string | null
  >(null);
  const [benchmarkAssetsRefreshKey, setBenchmarkAssetsRefreshKey] =
    useState<number>(0);
  const importInputRef = useRef<HTMLInputElement>(null);

  const {
    benchmark,
    setBenchmark,
    isDemo,
    setIsDemo,
    existingTestPk,
    setExistingTestPk,
    isSavingTest,
    saveMessage,
    saveError,
    setSaveError,
    saveTest,
  } = useAssessmentBuilderSave({
    search,
    token,
    apiUrl,
    testType: "reading",
    difficultyLevel: meta.difficultyLevel,
    buildPayload: ({
      existingTestPk: nextExistingTestPk,
      benchmark: nextBenchmark,
    }) => ({
      is_demo: isDemo ? JSON.parse(isDemo) : undefined,
      pk: nextExistingTestPk || undefined,
      difficulty_level: meta.difficultyLevel,
      test_type: "reading",
      status: "draft",
      benchmark: nextBenchmark,
      meta: toReadingSaveMeta(meta),
      parts: parts.map((part, index) => ({
        part: index + 1,
        title: part.title,
        prompt_image: part.promptImage,
        text_passage: part.passage,
        maze_mask_text: part.mazeMaskText,
        vocab_mask_text: part.vocabularyMaskText,
        questions: {
          maze: part.mazeBank.map((question) => ({
            id: question.id,
            placeholderId: question.placeholderId,
            placeholder_id: question.placeholderId,
            correctWord: question.correctWord,
            options: question.options,
          })),
          vocabulary: part.vocabularyBank.map((question) => ({
            ...question,
            placeholder_id: question.placeholderId,
          })),
        },
      })),
    }),
  });

  const canListBenchmarkAssets = Boolean(apiUrl && token && benchmark.trim());
  const imageMediaLibrary = mediaLibrary.filter(
    (item) => item.assetType === "images",
  );
  const audioMediaLibrary = mediaLibrary.filter(
    (item) => item.assetType === "audio",
  );
  const mazeQuestionKey = (partIndex: number, id: string) =>
    `part-${partIndex}-maze-${id}`;
  const vocabularyQuestionKey = (partIndex: number, id: string) =>
    `part-${partIndex}-vocab-${id}`;
  const mazePreviewKey = (partIndex: number, id: string) =>
    `preview-part-${partIndex}-maze-${id}`;
  const vocabularyPreviewKey = (partIndex: number, id: string) =>
    `preview-part-${partIndex}-vocab-${id}`;

  const isMazeCollapsed = (
    partIndex: number,
    question: MazeQuestion,
  ): boolean => {
    const key = mazeQuestionKey(partIndex, question.id);
    return collapsedMazeQuestions[key] ?? true;
  };

  const isVocabularyCollapsed = (
    partIndex: number,
    question: VocabularyQuestion,
  ): boolean => {
    const key = vocabularyQuestionKey(partIndex, question.id);
    return collapsedVocabularyQuestions[key] ?? true;
  };

  const toggleMazeCollapsed = (partIndex: number, question: MazeQuestion) => {
    const key = mazeQuestionKey(partIndex, question.id);
    setCollapsedMazeQuestions((current) => ({
      ...current,
      [key]: !isMazeCollapsed(partIndex, question),
    }));
  };

  const toggleVocabularyCollapsed = (
    partIndex: number,
    question: VocabularyQuestion,
  ) => {
    const key = vocabularyQuestionKey(partIndex, question.id);
    setCollapsedVocabularyQuestions((current) => ({
      ...current,
      [key]: !isVocabularyCollapsed(partIndex, question),
    }));
  };

  const isMazePreviewVisible = (partIndex: number, questionId: string) =>
    visibleMazePreviews[mazePreviewKey(partIndex, questionId)] ?? false;

  const toggleMazePreview = (partIndex: number, questionId: string) => {
    const key = mazePreviewKey(partIndex, questionId);
    setVisibleMazePreviews((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const isVocabularyPreviewVisible = (partIndex: number, questionId: string) =>
    visibleVocabularyPreviews[vocabularyPreviewKey(partIndex, questionId)] ??
    false;

  const toggleVocabularyPreview = (partIndex: number, questionId: string) => {
    const key = vocabularyPreviewKey(partIndex, questionId);
    setVisibleVocabularyPreviews((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const updateMeta = (field: keyof ReadingAssessmentMeta, value: string) => {
    setMeta((current) => ({ ...current, [field]: value }));
  };

  const updatePartField = (
    partIndex: number,
    field:
      | "title"
      | "promptImage"
      | "passage"
      | "mazeMaskText"
      | "vocabularyMaskText",
    value: string,
  ) => {
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex ? { ...part, [field]: value } : part,
      ),
    );
  };

  const updateMazeQuestion = (
    partIndex: number,
    id: string,
    field: keyof MazeQuestion,
    value: string,
  ) => {
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex
          ? {
              ...part,
              mazeBank: part.mazeBank.map((question) =>
                question.id === id ? { ...question, [field]: value } : question,
              ),
            }
          : part,
      ),
    );
  };

  const updateMazeOption = (
    partIndex: number,
    id: string,
    optionIndex: number,
    value: string,
  ) => {
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex
          ? {
              ...part,
              mazeBank: part.mazeBank.map((question) =>
                question.id === id
                  ? {
                      ...question,
                      options: question.options.map((option, index) =>
                        index === optionIndex ? value : option,
                      ) as [string, string, string, string],
                    }
                  : question,
              ),
            }
          : part,
      ),
    );
  };

  const updateVocabularyQuestion = (
    partIndex: number,
    questionId: string,
    field: keyof VocabularyQuestion,
    value: string,
  ) => {
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex
          ? {
              ...part,
              vocabularyBank: part.vocabularyBank.map((question) =>
                question.id === questionId
                  ? { ...question, [field]: value }
                  : question,
              ),
            }
          : part,
      ),
    );
  };

  const addMazeQuestion = (partIndex: number) => {
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex
          ? {
              ...part,
              mazeBank: [...part.mazeBank, createMazeQuestion()],
            }
          : part,
      ),
    );
  };

  const setAllMazeCollapsedForPart = (
    partIndex: number,
    collapsed: boolean,
  ) => {
    setCollapsedMazeQuestions((current) => {
      const next = { ...current };
      const mazeBank = parts[partIndex]?.mazeBank ?? [];
      for (const question of mazeBank) {
        next[mazeQuestionKey(partIndex, question.id)] = collapsed;
      }
      return next;
    });
  };

  const removeMazeQuestion = (partIndex: number, id: string) => {
    const key = mazeQuestionKey(partIndex, id);
    setCollapsedMazeQuestions((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex
          ? {
              ...part,
              mazeBank:
                part.mazeBank.length > 1
                  ? part.mazeBank.filter((question) => question.id !== id)
                  : part.mazeBank,
            }
          : part,
      ),
    );
  };

  const addVocabularyQuestion = (partIndex: number) => {
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex
          ? {
              ...part,
              vocabularyBank: [
                ...part.vocabularyBank,
                createVocabularyQuestion(),
              ],
            }
          : part,
      ),
    );
  };

  const setAllVocabularyCollapsedForPart = (
    partIndex: number,
    collapsed: boolean,
  ) => {
    setCollapsedVocabularyQuestions((current) => {
      const next = { ...current };
      const vocabularyBank = parts[partIndex]?.vocabularyBank ?? [];
      for (const question of vocabularyBank) {
        next[vocabularyQuestionKey(partIndex, question.id)] = collapsed;
      }
      return next;
    });
  };

  const removeVocabularyQuestion = (partIndex: number, id: string) => {
    const key = vocabularyQuestionKey(partIndex, id);
    setCollapsedVocabularyQuestions((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex
          ? {
              ...part,
              vocabularyBank:
                part.vocabularyBank.length > 1
                  ? part.vocabularyBank.filter((question) => question.id !== id)
                  : part.vocabularyBank,
            }
          : part,
      ),
    );
  };

  const handleExportTest = () => {
    const maskValidationErrors = validateReadingMasksExactMatchPassages(parts);
    if (maskValidationErrors.length > 0) {
      setSaveError(formatReadingMaskValidationErrors(maskValidationErrors));
      return;
    }

    const payload = toReadingTransferFile(
      existingTestPk,
      benchmark.trim(),
      meta,
      parts,
    );
    const safeGrade = meta.difficultyLevel.trim() || "unknown-difficultyLevel";
    const filename = `reading-test-${safeGrade}-${new Date().toISOString().replaceAll(":", "-")}.json`;
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
  };

  const handleSaveTest = useCallback(() => {
    const maskValidationErrors = validateReadingMasksExactMatchPassages(parts);
    if (maskValidationErrors.length > 0) {
      setSaveError(formatReadingMaskValidationErrors(maskValidationErrors));
      return;
    }

    void saveTest();
  }, [parts, saveTest, setSaveError]);

  const handleImportClick = () => {
    setMoreMenuAnchorEl(null);
    setImportError(null);
    importInputRef.current?.click();
  };

  const handleOpenMoreMenu = (event: MouseEvent<HTMLElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const updateMazeSearch = (partIndex: number, value: string) => {
    setMazeSearchByPart((current) => ({ ...current, [partIndex]: value }));
  };

  const updateVocabularySearch = (partIndex: number, value: string) => {
    setVocabularySearchByPart((current) => ({
      ...current,
      [partIndex]: value,
    }));
  };

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const fileContent = await file.text();
      const parsed = JSON.parse(fileContent) as ReadingTestTransferFile;
      const test = parsed?.test;
      if (!test || !Array.isArray(test.parts)) {
        setImportMessage(null);
        setImportError("Invalid import file format.");
        return;
      }

      const importedPk = asString(test.pk).trim();
      const importedBenchmark = asString(test.benchmark).trim();
      const importedMetaRaw = (test.meta ?? {}) as Record<string, unknown>;
      const normalizedMeta: ReadingAssessmentMeta = {
        difficultyLevel:
          asString(test.difficulty_level) ||
          parseDifficultyLevelFromPK(importedPk),
        assessmentTitle: asString(importedMetaRaw.assessmentTitle),
        overallInstructions: asString(
          importedMetaRaw.overallInstructions ?? importedMetaRaw.instructions,
        ),
        instructionsAudio: asString(
          importedMetaRaw.instructionsAudio ??
            importedMetaRaw.instructions_audio,
        ),
        oralReadingInstructionAudio: asString(
          importedMetaRaw.oralReadingInstructionAudio ??
            importedMetaRaw.oral_reading_instruction_audio,
        ),
        instructionImage: asString(
          importedMetaRaw.instructionImage ?? importedMetaRaw.instruction_image,
        ),
        recapImage: asString(
          importedMetaRaw.recapImage ?? importedMetaRaw.recap_image,
        ),
        mazeInstructions: asString(
          importedMetaRaw.mazeInstructions ?? importedMetaRaw.maze_instructions,
        ),
        mazeInstructionsAudio: asString(
          importedMetaRaw.mazeInstructionsAudio ??
            importedMetaRaw.maze_instructions_audio,
        ),
        vocabQuestionInstructionAudio: asString(
          importedMetaRaw.vocabQuestionInstructionAudio ??
            importedMetaRaw.vocab_question_instruction_audio,
        ),
        recapInstructions: asString(
          importedMetaRaw.recapInstructions ??
            importedMetaRaw.recap_instructions,
        ),
        recapInstructionsAudio: asString(
          importedMetaRaw.recapInstructionsAudio ??
            importedMetaRaw.recap_instructions_audio,
        ),
      };

      const importedPartsRaw = test.parts as Array<Record<string, unknown>>;
      const normalizedParts = createInitialParts().map((basePart, index) => {
        const importedPart = importedPartsRaw[index] ?? {};
        const questions = (importedPart.questions ??
          importedPart.question_banks ??
          {}) as Record<string, unknown>;
        const nestedQuestions = (questions.questions ?? {}) as Record<
          string,
          unknown
        >;
        const importedMazeRaw = Array.isArray(questions.maze)
          ? questions.maze
          : Array.isArray(nestedQuestions.maze)
            ? nestedQuestions.maze
            : [];
        const importedVocabRaw = Array.isArray(questions.vocabulary)
          ? questions.vocabulary
          : Array.isArray(nestedQuestions.vocabulary)
            ? nestedQuestions.vocabulary
            : [];

        const normalizedMaze = importedMazeRaw.map((raw): MazeQuestion => {
          const item = raw as Record<string, unknown>;
          const id = asString(item.id) || createLocalId();
          return {
            id,
            placeholderId:
              asString(item.placeholderId ?? item.placeholder_id) || id,
            correctWord: asString(item.correctWord ?? item.correct_word),
            options: normalizeMazeOptionsInput(item.options),
          };
        });

        const normalizedVocab = importedVocabRaw.map(
          (raw): VocabularyQuestion => {
            const item = raw as Record<string, unknown>;
            const options = normalizeMazeOptionsInput(item.options);
            const id = asString(item.id) || createLocalId();
            return {
              id,
              placeholderId:
                asString(item.placeholderId ?? item.placeholder_id) || id,
              word: asString(item.word),
              prompt: asString(item.prompt),
              A: asString(item.A) || options[0],
              B: asString(item.B) || options[1],
              C: asString(item.C) || options[2],
              D: asString(item.D) || options[3],
              correctOption: normalizeCorrectOptionInput(
                item.correctOption ?? item.correct_option,
              ),
            };
          },
        );

        return {
          title: asString(importedPart.title) || `Part ${index + 1}`,
          promptImage: asString(
            importedPart.prompt_image ?? importedPart.promptImage,
          ),
          passage: asString(importedPart.text_passage ?? importedPart.passage),
          mazeMaskText: asString(importedPart.maze_mask_text),
          vocabularyMaskText: asString(importedPart.vocab_mask_text),
          mazeBank:
            normalizedMaze.length > 0 ? normalizedMaze : [createMazeQuestion()],
          vocabularyBank:
            normalizedVocab.length > 0
              ? normalizedVocab
              : [createVocabularyQuestion()],
        };
      });

      setMeta(normalizedMeta);
      setParts(normalizedParts);
      setCollapsedMazeQuestions({});
      setCollapsedVocabularyQuestions({});
      setActiveTab("meta");
      const importTarget = resolveReadingImportTarget({
        currentBenchmark: benchmark,
        importedBenchmark,
        importedPk,
        isDemo,
      });
      setBenchmark(importTarget.benchmark);
      setExistingTestPk(importTarget.existingTestPk);

      if (importTarget.existingTestPk) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("pk", importTarget.existingTestPk);
        currentUrl.searchParams.set("benchmark", importTarget.benchmark);
        window.history.replaceState(
          {},
          "",
          `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
      } else {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete("pk");
        currentUrl.searchParams.set("benchmark", importTarget.benchmark);
        window.history.replaceState(
          {},
          "",
          `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
      }

      setImportError(null);
      setImportMessage(
        `Imported ${file.name}. Review and click Save Test to write to this environment.`,
      );
      setSaveError(null);
    } catch (importTestError: unknown) {
      setImportMessage(null);
      setImportError(
        importTestError instanceof Error
          ? importTestError.message
          : "Unable to import JSON file.",
      );
    } finally {
      event.target.value = "";
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    const pk = params.get("pk")?.trim();
    const benchmarkFromQuery = params.get("benchmark")?.trim();
    if (benchmarkFromQuery) {
      setBenchmark(benchmarkFromQuery);
    }
    const isDemoFromQuery = params.get("is_demo")?.trim();
    if (isDemoFromQuery) {
      setIsDemo(isDemoFromQuery);
    }
    if (!pk || !token || !apiUrl) {
      setExistingTestPk("");
      return;
    }

    const controller = new AbortController();
    const loadExistingTest = async () => {
      try {
        setLoadingExistingTest(true);
        setExistingTestError(null);
        const response = await fetch(
          `${apiUrl}/content/reading?pk=${encodeURIComponent(pk)}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          setExistingTestError(`Unable to load test (${response.status})`);
          return;
        }

        const payload = (await response.json()) as TestDetailsResponse;
        const details = payload?.test;
        const resolvedPK = details?.pk ?? pk;
        setExistingTestPk(resolvedPK);

        const items = Array.isArray(details?.items) ? details.items : [];
        const metaItem =
          items.find((item) => asString(item.SK) === "META") ?? {};
        const partItems = items
          .filter(
            (item) =>
              asString(item.entity_type) === "test_part" ||
              asString(item.SK).startsWith("PART#"),
          )
          .sort((left, right) =>
            asString(left.SK).localeCompare(asString(right.SK)),
          );
        const questionItems = items.filter((item) => {
          const sk = asString(item.SK);
          return (
            sk.startsWith("QUESTION#") ||
            asString(item.entity_type) === "test_question"
          );
        });

        setMeta({
          difficultyLevel:
            asString(metaItem.difficulty_level) ||
            parseDifficultyLevelFromPK(resolvedPK),
          assessmentTitle:
            asString(metaItem.assessment_title ?? metaItem.title) ||
            `Reading ${asString(metaItem.difficulty_level) || ""}`.trim(),
          overallInstructions: asString(metaItem.instructions),
          instructionsAudio: asString(
            metaItem.instructions_audio ?? metaItem.instructionsAudio,
          ),
          oralReadingInstructionAudio: asString(
            metaItem.oral_reading_instruction_audio ??
              metaItem.oralReadingInstructionAudio,
          ),
          instructionImage: asString(
            metaItem.instruction_image ?? metaItem.instructionImage,
          ),
          recapImage: asString(metaItem.recap_image ?? metaItem.recapImage),
          mazeInstructions: asString(metaItem.maze_instructions),
          mazeInstructionsAudio: asString(
            metaItem.maze_instructions_audio ?? metaItem.mazeInstructionsAudio,
          ),
          vocabQuestionInstructionAudio: asString(
            metaItem.vocab_question_instruction_audio ??
              metaItem.vocabQuestionInstructionAudio,
          ),
          recapInstructions: asString(
            metaItem.recap_instructions ?? metaItem.recapInstructions,
          ),
          recapInstructionsAudio: asString(
            metaItem.recap_instructions_audio ??
              metaItem.recapInstructionsAudio,
          ),
        });
        setBenchmark(
          asString(metaItem.benchmark) ||
            parseBenchmarkFromPK(resolvedPK) ||
            benchmarkFromQuery ||
            "",
        );

        const nextParts = createInitialParts();
        nextParts.forEach((part, index) => {
          const partItem = partItems[index] ?? {};
          part.title = asString(partItem.title) || `Part ${index + 1}`;
          part.promptImage = asString(
            partItem.prompt_image ?? partItem.promptImage,
          );
          part.passage = asString(partItem.passage ?? partItem.text_passage);
          part.mazeMaskText = asString(partItem.maze_mask_text);
          part.vocabularyMaskText = asString(partItem.vocab_mask_text);
          part.mazeBank = [];
          part.vocabularyBank = [];
        });

        for (const item of questionItems) {
          const partIndex = partIndexFromQuestion(item);
          const options = extractOptions(item);
          const questionType = asString(item.question_type).toLowerCase();

          if (questionType === "maze") {
            const fallbackByOption =
              options[
                resolveCorrectOption(item) === "A"
                  ? 0
                  : resolveCorrectOption(item) === "B"
                    ? 1
                    : resolveCorrectOption(item) === "C"
                      ? 2
                      : 3
              ] ?? "";
            nextParts[partIndex]?.mazeBank.push({
              id: asString(item.question_id) || createLocalId(),
              placeholderId: asString(
                item.placeholder_id ?? item.placeholderId,
              ),
              correctWord: asString(item.correct_word) || fallbackByOption,
              options,
            });
          }

          if (questionType === "vocabulary") {
            const optionA = options[0] ?? "";
            const optionB = options[1] ?? "";
            const optionC = options[2] ?? "";
            const optionD = options[3] ?? "";
            nextParts[partIndex]?.vocabularyBank.push({
              id: asString(item.question_id) || createLocalId(),
              placeholderId: asString(
                item.placeholder_id ?? item.placeholderId,
              ),
              word: asString(item.word),
              prompt: asString(item.prompt),
              A: optionA,
              B: optionB,
              C: optionC,
              D: optionD,
              correctOption: resolveCorrectOption(item),
            });
          }
        }

        nextParts.forEach((part) => {
          if (part.mazeBank.length === 0) {
            part.mazeBank = [createMazeQuestion()];
          }
          if (part.vocabularyBank.length === 0) {
            part.vocabularyBank = [createVocabularyQuestion()];
          }
        });

        setParts(nextParts);
        setCollapsedMazeQuestions({});
        setCollapsedVocabularyQuestions({});
      } catch (loadError: unknown) {
        if (
          loadError instanceof DOMException &&
          loadError.name === "AbortError"
        ) {
          return;
        }
        setExistingTestError(
          loadError instanceof Error
            ? loadError.message
            : "Unable to load test details.",
        );
      } finally {
        setLoadingExistingTest(false);
      }
    };

    loadExistingTest().then();
    return () => controller.abort();
  }, [apiUrl, search, token, setExistingTestPk, setBenchmark, setIsDemo]);

  useEffect(() => {
    if (!canListBenchmarkAssets) {
      return;
    }

    const controller = new AbortController();
    listAssessmentBenchmarkAssetsRequest({
      apiUrl,
      token,
      benchmarkId: benchmark,
      isDemo: isDemo,
      signal: controller.signal,
    })
      .then((assets) => {
        const mediaItems = assets.map((asset) => {
          const isImage = asset.assetType === "images";
          return {
            id: asset.key,
            label: asset.fileName || asset.key,
            assetType: asset.assetType,
            url: asset.publicUrl,
            altText: asset.key,
            imageUrl: isImage ? asset.publicUrl : "",
            audioUrl: isImage ? "" : asset.publicUrl,
          };
        });
        setMediaLibrary(mediaItems);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setBenchmarkAssetsError(
          error instanceof Error
            ? error.message
            : "Unable to load benchmark assets.",
        );
      });

    return () => controller.abort();
  }, [
    apiUrl,
    benchmark,
    isDemo,
    benchmarkAssetsRefreshKey,
    canListBenchmarkAssets,
    token,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (!isSavingTest) {
          handleSaveTest();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSaveTest, isSavingTest]);

  const manageTestsUrl = benchmark.trim()
    ? `/assessments-admin/benchmarks/manage?benchmark=${encodeURIComponent(benchmark.trim())}${isDemo ? `&is_demo=${encodeURIComponent(isDemo)}` : ""}`
    : "/assessments-admin/benchmarks/manage";
  const isMoreMenuOpen = Boolean(moreMenuAnchorEl);
  const compactButtonSx = {
    px: 1.25,
    py: 0.5,
    minWidth: "auto",
  };
  const compactEditorFieldSx = {
    "& .MuiButton-root": compactButtonSx,
    "& .MuiInputBase-root": {
      minHeight: 40,
    },
    "& .MuiOutlinedInput-input": {
      paddingTop: "8.5px",
      paddingBottom: "8.5px",
      lineHeight: 1.25,
    },
    "& .MuiInputBase-inputMultiline": {
      paddingTop: 0,
      paddingBottom: 0,
    },
    "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
      alignItems: "flex-start",
      py: 1,
    },
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 3,
        pb: 14,
        ...compactEditorFieldSx,
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Box>
            <Link
              component={RouterLink}
              to={manageTestsUrl}
              underline="hover"
              sx={{ display: "inline-block", mb: 1.5, fontSize: 14 }}
            >
              Back to dashboard
            </Link>
            <Typography variant="h4" component="h1" gutterBottom>
              Reading Assessment Builder
            </Typography>
          </Box>

          <IconButton
            aria-label="More actions"
            aria-controls={
              isMoreMenuOpen ? "reading-builder-actions" : undefined
            }
            aria-haspopup="true"
            aria-expanded={isMoreMenuOpen ? "true" : undefined}
            onClick={handleOpenMoreMenu}
          >
            <MoreVertIcon />
          </IconButton>
        </Stack>

        <Menu
          id="reading-builder-actions"
          anchorEl={moreMenuAnchorEl}
          open={isMoreMenuOpen}
          onClose={handleCloseMoreMenu}
        >
          <MenuItem
            onClick={() => {
              handleCloseMoreMenu();
              handleExportTest();
            }}
          >
            Export JSON
          </MenuItem>
          <MenuItem onClick={handleImportClick}>Import JSON</MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseMoreMenu();
              setIsAssetUploadDialogOpen(true);
            }}
          >
            Upload to S3
          </MenuItem>
        </Menu>

        <BenchmarkAssetUploadDialog
          open={isAssetUploadDialogOpen}
          apiUrl={apiUrl}
          token={token}
          benchmarkId={benchmark}
          onClose={() => setIsAssetUploadDialogOpen(false)}
          onUploadComplete={() =>
            setBenchmarkAssetsRefreshKey((current) => current + 1)
          }
          isDemo={isDemo ? JSON.parse(isDemo) : false}
        />

        <Typography variant="body2" color="text.secondary">
          Benchmark: <Box component="code">{benchmark}</Box>
        </Typography>
        {existingTestPk ? (
          <Typography variant="body2" color="text.secondary">
            Editing test: <Box component="code">{existingTestPk}</Box>
          </Typography>
        ) : null}
        {existingTestPk ? (
          <Typography variant="body2" color="text.secondary">
            Current difficulty level:{" "}
            <strong>{meta.difficultyLevel || "-"}</strong>
          </Typography>
        ) : null}
        {loadingExistingTest ? (
          <Typography variant="body2" color="text.secondary">
            Loading existing test data...
          </Typography>
        ) : null}
        {existingTestError ? (
          <Alert severity="error">{existingTestError}</Alert>
        ) : null}
        {benchmarkAssetsError ? (
          <Alert severity="error">{benchmarkAssetsError}</Alert>
        ) : null}

        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: "none" }}
          onChange={handleImportFile}
        />

        {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
        {saveError ? <Alert severity="error">{saveError}</Alert> : null}
        {importMessage ? <Alert severity="info">{importMessage}</Alert> : null}
        {importError ? <Alert severity="error">{importError}</Alert> : null}

        <Tabs
          value={activeTab}
          onChange={(_event, value: string) => setActiveTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
          aria-label="Reading assessment sections"
        >
          <Tab value="meta" label="Meta" />
          {parts.map((part, partIndex) => {
            const tabId = `part-${partIndex}`;
            return (
              <Tab
                key={tabId}
                value={tabId}
                label={part.title || `Part ${partIndex + 1}`}
              />
            );
          })}
        </Tabs>

        {activeTab === "meta" ? (
          <Paper
            variant="outlined"
            sx={{ p: 2.5, "& .MuiTextField-root": { minWidth: 0 } }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Difficulty Level"
                value={meta.difficultyLevel}
                onChange={(event) =>
                  updateMeta("difficultyLevel", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">Select difficulty level</MenuItem>
                {difficultyLevelOptions.map((difficultyLevelOption) => (
                  <MenuItem
                    key={difficultyLevelOption.value}
                    value={difficultyLevelOption.value}
                  >
                    {difficultyLevelOption.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Assessment Title"
                value={meta.assessmentTitle}
                onChange={(event) =>
                  updateMeta("assessmentTitle", event.target.value)
                }
                fullWidth
              />
              <TextField
                label="Overall Test Instructions"
                value={meta.overallInstructions}
                onChange={(event) =>
                  updateMeta("overallInstructions", event.target.value)
                }
                multiline
                rows={3}
                fullWidth
                sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}
              />

              <TextField
                select
                label="Instruction Image"
                value={meta.instructionImage}
                onChange={(event) =>
                  updateMeta("instructionImage", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {imageMediaLibrary.map((asset) => (
                  <MenuItem
                    key={asset.id}
                    value={asset.imageUrl}
                    sx={{ gap: 2 }}
                  >
                    <img src={asset.imageUrl} alt={asset.altText} height={16} />
                    {asset.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Instruction Audio"
                value={meta.instructionsAudio}
                onChange={(event) =>
                  updateMeta("instructionsAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {audioMediaLibrary.map((asset) => (
                  <MenuItem key={asset.id} value={asset.audioUrl}>
                    {asset.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Maze Question Instructions"
                value={meta.mazeInstructions}
                onChange={(event) =>
                  updateMeta("mazeInstructions", event.target.value)
                }
                multiline
                rows={2}
                fullWidth
                sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}
              />
              <TextField
                select
                label="Oral Reading Instructions Audio"
                value={meta.oralReadingInstructionAudio}
                onChange={(event) =>
                  updateMeta("oralReadingInstructionAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {audioMediaLibrary.map((asset) => (
                  <MenuItem key={asset.id} value={asset.audioUrl}>
                    {asset.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Maze Question Instructions Audio"
                value={meta.mazeInstructionsAudio}
                onChange={(event) =>
                  updateMeta("mazeInstructionsAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {audioMediaLibrary.map((asset) => (
                  <MenuItem key={asset.id} value={asset.audioUrl}>
                    {asset.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Vocabulary Question Instructions Audio"
                value={meta.vocabQuestionInstructionAudio}
                onChange={(event) =>
                  updateMeta(
                    "vocabQuestionInstructionAudio",
                    event.target.value,
                  )
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {audioMediaLibrary.map((asset) => (
                  <MenuItem key={asset.id} value={asset.audioUrl}>
                    {asset.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Recap Instructions"
                value={meta.recapInstructions}
                onChange={(event) =>
                  updateMeta("recapInstructions", event.target.value)
                }
                multiline
                rows={2}
                fullWidth
                sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}
              />
              <TextField
                select
                label="Recap Image"
                value={meta.recapImage}
                onChange={(event) =>
                  updateMeta("recapImage", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {imageMediaLibrary.map((asset) => (
                  <MenuItem
                    key={asset.id}
                    value={asset.imageUrl}
                    sx={{ gap: 2 }}
                  >
                    <img src={asset.imageUrl} alt={asset.altText} height={16} />
                    {asset.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Recap Instructions Audio"
                value={meta.recapInstructionsAudio}
                onChange={(event) =>
                  updateMeta("recapInstructionsAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {audioMediaLibrary.map((asset) => (
                  <MenuItem key={asset.id} value={asset.audioUrl}>
                    {asset.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mt: 3, mb: 2 }}
            >
              <Typography variant="h6">
                Test Media Library ({mediaLibrary.length})
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  value={mediaLibrarySearch}
                  onChange={(event) =>
                    setMediaLibrarySearch(event.target.value)
                  }
                  placeholder="Search by label, alt text, URL, or ID"
                  size="small"
                  sx={{ minWidth: { sm: 300 } }}
                />
              </Stack>
            </Stack>

            {(() => {
              const filteredMedia = mediaLibrary.filter((item, index) =>
                matchesSearch(mediaLibrarySearch, [
                  index + 1,
                  item.id,
                  item.label,
                  item.altText,
                  item.imageUrl,
                  item.audioUrl,
                  item.assetType,
                ]),
              );

              if (filteredMedia.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No media items match the current search.
                  </Typography>
                );
              }

              return (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(4, minmax(0, 1fr))",
                      md: "repeat(10, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  {filteredMedia.map((item, index) => {
                    return (
                      <Paper
                        key={item.id}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          minHeight: 110,
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 64,
                            mb: 0.75,
                          }}
                        >
                          {item.assetType === "images" && item.imageUrl ? (
                            <Box
                              component="img"
                              src={item.imageUrl}
                              alt={
                                item.altText || item.label || "Media preview"
                              }
                              sx={{
                                display: "block",
                                maxWidth: "100%",
                                maxHeight: 56,
                                width: "auto",
                                borderRadius: 1,
                                objectFit: "contain",
                              }}
                            />
                          ) : item.assetType === "audio" && item.audioUrl ? (
                            <Box
                              component="audio"
                              controls
                              src={item.audioUrl}
                              sx={{ width: "100%" }}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No preview
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.label || `Media ${index + 1}`}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              );
            })()}
          </Paper>
        ) : null}

        {parts.map((part, partIndex) => {
          const tabId = `part-${partIndex}`;
          if (activeTab !== tabId) {
            return null;
          }

          return (
            <Paper
              key={`part-section-${partIndex}`}
              variant="outlined"
              sx={{ p: 2.5, "& .MuiTextField-root": { minWidth: 0 } }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  label="Part Title"
                  value={part.title}
                  onChange={(event) =>
                    updatePartField(partIndex, "title", event.target.value)
                  }
                  fullWidth
                />
                <TextField
                  select
                  label="Oral Reading Image"
                  value={part.promptImage}
                  onChange={(event) =>
                    updatePartField(
                      partIndex,
                      "promptImage",
                      event.target.value,
                    )
                  }
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {imageMediaLibrary.map((asset) => (
                    <MenuItem
                      key={asset.id}
                      value={asset.imageUrl}
                      sx={{ gap: 2 }}
                    >
                      <img
                        src={asset.imageUrl}
                        alt={asset.altText}
                        height={16}
                      />
                      {asset.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Text Passage"
                  value={part.passage}
                  onChange={(event) =>
                    updatePartField(partIndex, "passage", event.target.value)
                  }
                  multiline
                  rows={4}
                  fullWidth
                  sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}
                />
              </Box>

              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1}
                sx={{ mt: 3, mb: 2 }}
              >
                <Typography variant="h6">
                  Part {partIndex + 1} Maze Bank
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    value={mazeSearchByPart[partIndex] ?? ""}
                    onChange={(event) =>
                      updateMazeSearch(partIndex, event.target.value)
                    }
                    placeholder="Search by ID, word, or option"
                    size="small"
                    sx={{ minWidth: { sm: 260 } }}
                  />
                  <Button
                    type="button"
                    size="medium"
                    variant="outlined"
                    onClick={() => setAllMazeCollapsedForPart(partIndex, true)}
                  >
                    Collapse All
                  </Button>
                  <Button
                    type="button"
                    size="medium"
                    variant="outlined"
                    onClick={() => setAllMazeCollapsedForPart(partIndex, false)}
                  >
                    Expand All
                  </Button>
                  <Button
                    type="button"
                    size="medium"
                    variant="outlined"
                    onClick={() => addMazeQuestion(partIndex)}
                  >
                    Add Maze Question
                  </Button>
                </Stack>
              </Stack>

              <TextField
                label="Maze Mask Text"
                value={part.mazeMaskText}
                onChange={(event) =>
                  updatePartField(partIndex, "mazeMaskText", event.target.value)
                }
                multiline
                rows={4}
                fullWidth
                sx={{ gridColumn: { xs: "auto", lg: "1 / -1" }, mb: 2 }}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(4, minmax(0, 1fr))",
                    md: "repeat(10, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {part.mazeBank
                  .filter((question, questionIndex) =>
                    matchesSearch(mazeSearchByPart[partIndex] ?? "", [
                      questionIndex + 1,
                      question.id,
                      question.placeholderId,
                      question.correctWord,
                      ...question.options,
                    ]),
                  )
                  .map((question, questionIndex) => {
                    const collapsed = isMazeCollapsed(partIndex, question);
                    if (collapsed) {
                      return (
                        <Paper
                          key={question.id}
                          variant="outlined"
                          sx={{
                            p: 1,
                            minHeight: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            toggleMazeCollapsed(partIndex, question)
                          }
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            {question.id || `Q${questionIndex + 1}`}{" "}
                            {question.placeholderId
                              ? `(${question.placeholderId}) `
                              : ""}
                            {question.correctWord
                              ? `• ${question.correctWord}`
                              : ""}
                          </Typography>
                        </Paper>
                      );
                    }

                    return (
                      <Paper
                        key={question.id}
                        variant="outlined"
                        sx={{ p: 1.75, gridColumn: "1 / -1" }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="subtitle1">
                            Maze Question {questionIndex + 1}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button
                              type="button"
                              size="medium"
                              variant="outlined"
                              onClick={() =>
                                toggleMazeCollapsed(partIndex, question)
                              }
                            >
                              Collapse
                            </Button>
                            <Button
                              type="button"
                              size="medium"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                removeMazeQuestion(partIndex, question.id)
                              }
                              disabled={part.mazeBank.length === 1}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Stack>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 2,
                          }}
                        >
                          <TextField
                            label="Question ID"
                            value={question.id}
                            slotProps={{ input: { readOnly: true } }}
                            fullWidth
                          />
                          <TextField
                            label="Mask Placeholder ID"
                            value={question.placeholderId}
                            onChange={(event) =>
                              updateMazeQuestion(
                                partIndex,
                                question.id,
                                "placeholderId",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Correct Word"
                            value={question.correctWord}
                            onChange={(event) =>
                              updateMazeQuestion(
                                partIndex,
                                question.id,
                                "correctWord",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Option 1"
                            value={question.options[0]}
                            onChange={(event) =>
                              updateMazeOption(
                                partIndex,
                                question.id,
                                0,
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Option 2"
                            value={question.options[1]}
                            onChange={(event) =>
                              updateMazeOption(
                                partIndex,
                                question.id,
                                1,
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Option 3"
                            value={question.options[2]}
                            onChange={(event) =>
                              updateMazeOption(
                                partIndex,
                                question.id,
                                2,
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Option 4"
                            value={question.options[3]}
                            onChange={(event) =>
                              updateMazeOption(
                                partIndex,
                                question.id,
                                3,
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                        </Box>
                        <AssessmentQuestionPreview
                          isVisible={isMazePreviewVisible(
                            partIndex,
                            question.id,
                          )}
                          onToggle={() =>
                            toggleMazePreview(partIndex, question.id)
                          }
                        >
                          <ReadingMazePreview
                            assessment={buildMazePreviewAssessment(
                              meta,
                              mediaLibrary,
                              part,
                              partIndex,
                              question,
                              questionIndex,
                            )}
                            isLoading={false}
                            onStepReadyChange={() => {}}
                            onSaveAnswer={() => {}}
                          />
                        </AssessmentQuestionPreview>
                      </Paper>
                    );
                  })}
                {part.mazeBank.filter((question, questionIndex) =>
                  matchesSearch(mazeSearchByPart[partIndex] ?? "", [
                    questionIndex + 1,
                    question.id,
                    question.placeholderId,
                    question.correctWord,
                    ...question.options,
                  ]),
                ).length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ gridColumn: "1 / -1" }}
                  >
                    No maze questions match the current search.
                  </Typography>
                ) : null}
              </Box>

              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1}
                sx={{ mt: 3, mb: 2 }}
              >
                <Typography variant="h6">
                  Part {partIndex + 1} Vocabulary Bank
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    value={vocabularySearchByPart[partIndex] ?? ""}
                    onChange={(event) =>
                      updateVocabularySearch(partIndex, event.target.value)
                    }
                    placeholder="Search by word, prompt, option, or ID"
                    size="small"
                    sx={{ minWidth: { sm: 300 } }}
                  />
                  <Button
                    type="button"
                    size="medium"
                    variant="outlined"
                    onClick={() =>
                      setAllVocabularyCollapsedForPart(partIndex, true)
                    }
                  >
                    Collapse All
                  </Button>
                  <Button
                    type="button"
                    size="medium"
                    variant="outlined"
                    onClick={() =>
                      setAllVocabularyCollapsedForPart(partIndex, false)
                    }
                  >
                    Expand All
                  </Button>
                  <Button
                    type="button"
                    size="medium"
                    variant="outlined"
                    onClick={() => addVocabularyQuestion(partIndex)}
                  >
                    Add Vocabulary Question
                  </Button>
                </Stack>
              </Stack>

              <TextField
                label="Vocabulary Mask Text"
                value={part.vocabularyMaskText}
                onChange={(event) =>
                  updatePartField(
                    partIndex,
                    "vocabularyMaskText",
                    event.target.value,
                  )
                }
                multiline
                rows={4}
                fullWidth
                sx={{ gridColumn: { xs: "auto", lg: "1 / -1" }, mb: 2 }}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(4, minmax(0, 1fr))",
                    md: "repeat(10, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {part.vocabularyBank
                  .filter((question, questionIndex) =>
                    matchesSearch(vocabularySearchByPart[partIndex] ?? "", [
                      questionIndex + 1,
                      question.id,
                      question.placeholderId,
                      question.word,
                      question.prompt,
                      question.A,
                      question.B,
                      question.C,
                      question.D,
                    ]),
                  )
                  .map((question, questionIndex) => {
                    const collapsed = isVocabularyCollapsed(
                      partIndex,
                      question,
                    );
                    if (collapsed) {
                      return (
                        <Paper
                          key={question.id}
                          variant="outlined"
                          sx={{
                            p: 1,
                            minHeight: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            toggleVocabularyCollapsed(partIndex, question)
                          }
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            {question.id || `Q${questionIndex + 1}`}{" "}
                            {question.placeholderId
                              ? `(${question.placeholderId}) `
                              : ""}
                            {question.word ? `• ${question.word}` : ""}
                          </Typography>
                        </Paper>
                      );
                    }

                    return (
                      <Paper
                        key={question.id}
                        variant="outlined"
                        sx={{ p: 1.75, gridColumn: "1 / -1" }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="subtitle1">
                            Vocabulary Question {questionIndex + 1}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button
                              type="button"
                              size="medium"
                              variant="outlined"
                              onClick={() =>
                                toggleVocabularyCollapsed(partIndex, question)
                              }
                            >
                              Collapse
                            </Button>
                            <Button
                              type="button"
                              size="medium"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                removeVocabularyQuestion(partIndex, question.id)
                              }
                              disabled={part.vocabularyBank.length === 1}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Stack>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 2,
                          }}
                        >
                          <TextField
                            label="Question ID"
                            value={question.id}
                            slotProps={{ input: { readOnly: true } }}
                            fullWidth
                          />
                          <TextField
                            label="Mask Placeholder ID"
                            value={question.placeholderId}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "placeholderId",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Target Word"
                            value={question.word}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "word",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Prompt"
                            value={question.prompt}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "prompt",
                                event.target.value,
                              )
                            }
                            multiline
                            rows={2}
                            fullWidth
                            sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}
                          />
                          <TextField
                            label="Option A"
                            value={question.A}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "A",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Option B"
                            value={question.B}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "B",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Option C"
                            value={question.C}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "C",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            label="Option D"
                            value={question.D}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "D",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                          <TextField
                            select
                            label="Correct Option"
                            value={question.correctOption}
                            onChange={(event) =>
                              updateVocabularyQuestion(
                                partIndex,
                                question.id,
                                "correctOption",
                                event.target
                                  .value as VocabularyQuestion["correctOption"],
                              )
                            }
                            fullWidth
                          >
                            <MenuItem value="A">A</MenuItem>
                            <MenuItem value="B">B</MenuItem>
                            <MenuItem value="C">C</MenuItem>
                            <MenuItem value="D">D</MenuItem>
                          </TextField>
                        </Box>
                        <AssessmentQuestionPreview
                          isVisible={isVocabularyPreviewVisible(
                            partIndex,
                            question.id,
                          )}
                          onToggle={() =>
                            toggleVocabularyPreview(partIndex, question.id)
                          }
                        >
                          <ReadingVocabularyPreview
                            assessment={buildVocabularyPreviewAssessment(
                              meta,
                              mediaLibrary,
                              part,
                              partIndex,
                              question,
                              questionIndex,
                            )}
                            isLoading={false}
                            onStepReadyChange={() => {}}
                            onSaveAnswer={() => {}}
                          />
                        </AssessmentQuestionPreview>
                      </Paper>
                    );
                  })}
                {part.vocabularyBank.filter((question, questionIndex) =>
                  matchesSearch(vocabularySearchByPart[partIndex] ?? "", [
                    questionIndex + 1,
                    question.id,
                    question.word,
                    question.prompt,
                    question.A,
                    question.B,
                    question.C,
                    question.D,
                  ]),
                ).length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ gridColumn: "1 / -1" }}
                  >
                    No vocabulary questions match the current search.
                  </Typography>
                ) : null}
              </Box>
            </Paper>
          );
        })}
      </Stack>
      <Button
        type="button"
        size="large"
        variant="contained"
        onClick={handleSaveTest}
        disabled={isSavingTest}
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          zIndex: (theme) => theme.zIndex.tooltip,
          minWidth: 160,
          boxShadow: 6,
        }}
      >
        {isSavingTest ? "Saving..." : "Save Test"}
      </Button>
    </Container>
  );
}
